import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  Collapse,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight, Search } from "@mui/icons-material";
import { formatCurrency } from "@/utils/format";
import { useDebounce } from "@/hooks/useDebounce";
import {
  chaveDoFiltro,
  DICA_ESTIMATIVA_PERDA,
  DIMENSAO_LABELS,
  DIMENSOES,
  ehSemValor,
  filtroDaDimensao,
  getRupturaProdutos,
  PROBLEMAS,
  PRODUTOS_POR_PAGINA,
  resolveEmpresaRef,
  SITUACOES,
  type RupturaDimensao,
  type RupturaFiltros,
  type RupturaProduto,
} from "@/api/ruptura.service";
import { GradeInfo } from "./RupturaCards";
import { formatNumero, ProblemaChip, RodapeCard, SEM_FILTRO, Voltar, type NomeLoja } from "./rupturaComum";
import { LojasRupturaContext, useRupturaResumo } from "./useRupturaBusca";

// Lista de produtos da ruptura, com busca e filtros.

function ProdutoCard({
  produto,
  nomeLoja,
  onVerDetalhe,
}: {
  produto: RupturaProduto;
  nomeLoja: NomeLoja;
  onVerDetalhe: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const solucao = produto.solucao
    ? resolveEmpresaRef(produto.solucao, produto.cod_empresa_ref ? nomeLoja(produto.cod_empresa_ref) : null)
    : "";

  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => setExpanded((v) => !v)} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ textTransform: "uppercase", color: "text.secondary" }} noWrap>
              {produto.descricao}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cód. {produto.cod_produto} · {nomeLoja(produto.cod_empresa)}
            </Typography>
          </Box>
          {produto.valor_risco ? (
            <Typography variant="h6" color="error.main" sx={{ whiteSpace: "nowrap" }}>
              {formatCurrency(produto.valor_risco)}
            </Typography>
          ) : null}
          <ProblemaChip codProblema={produto.cod_problema} nome={produto.problema} />
        </Stack>
      </CardActionArea>
      {/* Fora do CardActionArea, como nos cards da tela inicial. */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <GradeInfo
            mt={0}
            linhas={[
              { label: "Estoque Atual", valor: `${formatNumero(produto.estoque_disponivel)} un.` },
              { label: "Estoque Mínimo", valor: `${formatNumero(produto.qt_min)} un.` },
              { label: "Estoque Máximo", valor: `${formatNumero(produto.qt_max)} un.` },
              { label: "Média Venda Diária", valor: `${formatNumero(produto.mdv)} un./dia` },
              ...(produto.cobertura !== null ? [{ label: "Dias de Cobertura", valor: `${formatNumero(produto.cobertura, 1)} dias` }] : []),
              { label: "Categoria", valor: produto.categoria },
              { label: "Comprador", valor: produto.nome_comprador },
              ...(solucao ? [{ label: "Problema", valor: solucao }, { label: "Responsável", valor: produto.responsavel }] : []),
              { label: "Fornecedor", valor: produto.fornecedor },
              ...(produto.acao_sugerida ? [{ label: "Ação Sugerida", valor: produto.acao_sugerida }] : []),
              ...(produto.valor_risco
                ? [{ label: "Estimativa de Perda", valor: formatCurrency(produto.valor_risco), cor: "error.main", dica: DICA_ESTIMATIVA_PERDA }]
                : []),
            ]}
          />
        </Box>
      </Collapse>

      {expanded && <RodapeCard label="Ver detalhe →" onClick={onVerDetalhe} />}
    </Card>
  );
}

// Filtro da lista de produtos com busca: digitar filtra as opções; vazio = todos.
type OpcaoFiltro = { valor: string; rotulo: string };

function FiltroAutocomplete({
  label,
  valor,
  opcoes,
  onChange,
  loading = false,
  erro = null,
}: {
  label: string;
  valor: string;
  opcoes: OpcaoFiltro[];
  onChange: (valor: string) => void;
  loading?: boolean;
  erro?: string | null;
}) {
  return (
    <Autocomplete
      size="small"
      options={opcoes}
      value={opcoes.find((o) => o.valor === valor) ?? null}
      onChange={(_, o) => onChange(o?.valor ?? "")}
      getOptionLabel={(o) => o.rotulo}
      isOptionEqualToValue={(o, v) => o.valor === v.valor}
      renderOption={({ key: _key, ...props }, o) => (
        <li key={o.valor} {...props}>
          {o.rotulo}
        </li>
      )}
      loading={loading}
      loadingText="Carregando..."
      noOptionsText={erro ?? "Nenhuma opção"}
      renderInput={(params) => <TextField {...params} placeholder={label} />}
      sx={{ minWidth: 220, maxWidth: 320, flex: "1 1 220px" }}
    />
  );
}

// Filtro extra da lista de produtos: as opções são as linhas do resumo da dimensão.
function FiltroOpcoes({
  dimensao,
  valor,
  onChange,
  clientId,
  nomeLoja,
  filtro = SEM_FILTRO,
}: {
  dimensao: RupturaDimensao;
  valor: string;
  onChange: (valor: string) => void;
  clientId: string;
  nomeLoja: NomeLoja;
  // Com responsável escolhido, o filtro de problema só oferece os problemas dele.
  filtro?: RupturaFiltros;
}) {
  const { dados, erro, loading } = useRupturaResumo(dimensao, filtro, clientId);

  const opcoes = useMemo(
    () =>
      (dados ?? [])
        .map((o) => ({ valor: o.chave, rotulo: dimensao === "loja" ? nomeLoja(o.chave) : o.rotulo }))
        // "Sem X" é opção como as outras (a lista pode chegar filtrada por ela), só que no fim.
        .sort(
          (a, b) =>
            Number(ehSemValor(dimensao, a.valor)) - Number(ehSemValor(dimensao, b.valor)) ||
            a.rotulo.localeCompare(b.rotulo, "pt-BR", { numeric: true, sensitivity: "base" }),
        ),
    [dados, dimensao, nomeLoja],
  );

  return (
    <FiltroAutocomplete
      label={DIMENSAO_LABELS[dimensao]}
      valor={valor}
      opcoes={opcoes}
      onChange={onChange}
      loading={loading}
      erro={erro}
    />
  );
}

export function ProdutosView({
  filtrosIniciais,
  clientId,
  nomeLoja,
  onVoltar,
  onVerDetalhe,
}: {
  // Vindos do atalho que abriu a lista (linha e card de origem + situação da linha da grade);
  // todos aparecem nos filtros e o usuário pode trocar qualquer um.
  filtrosIniciais: RupturaFiltros;
  clientId: string;
  nomeLoja: NomeLoja;
  onVoltar: () => void;
  onVerDetalhe: (p: RupturaProduto) => void;
}) {
  const [produtos, setProdutos] = useState<RupturaProduto[]>([]);
  const [pagina, setPagina] = useState(0);
  const [temMais, setTemMais] = useState(false);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  // busca = o que está digitado; buscaAplicada = o que vai na query (após o debounce).
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  // "" = todas as situações.
  const [situacao, setSituacao] = useState(filtrosIniciais.cod_problema ? String(filtrosIniciais.cod_problema) : "");
  // Filtros das dimensões; a situação fica à parte.
  const [filtros, setFiltros] = useState<RupturaFiltros>(() => ({ ...filtrosIniciais, cod_problema: null }));
  const temFiltro = !!situacao || DIMENSOES.some((d) => chaveDoFiltro(filtros, d) !== null);

  const limparFiltros = () => {
    setSituacao("");
    setFiltros({});
    setPagina(0);
  };

  const aplicarBusca = useDebounce(
    useCallback((texto: string) => {
      setBuscaAplicada(texto);
      setPagina(0);
    }, []),
    500,
  );

  const lojas = useContext(LojasRupturaContext);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setErro(null);
    getRupturaProdutos(
      { pagina, busca: buscaAplicada, lojas, ...filtros, cod_problema: situacao ? Number(situacao) : null },
      clientId,
    )
      .then((novos) => {
        if (cancelado) return;
        setProdutos((atuais) => (pagina === 0 ? novos : [...atuais, ...novos]));
        setTemMais(novos.length === PRODUTOS_POR_PAGINA);
      })
      .catch((e) => !cancelado && setErro(e?.message ?? "Não foi possível carregar os produtos."))
      .finally(() => !cancelado && setLoading(false));
    return () => {
      cancelado = true;
    };
  }, [pagina, clientId, buscaAplicada, situacao, filtros, lojas]);

  return (
    <Stack gap={2}>
      <Voltar label="Voltar" onClick={onVoltar} />
      <Typography variant="subtitle1" fontWeight={700}>
        Produtos
      </Typography>

      <TextField
        size="small"
        placeholder="Nome, código ou código de barras do produto..."
        value={busca}
        onChange={(e) => {
          setBusca(e.target.value);
          aplicarBusca(e.target.value);
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        <FiltroAutocomplete
          label="Situação"
          valor={situacao}
          opcoes={SITUACOES.map((cod) => ({ valor: String(cod), rotulo: PROBLEMAS[cod]! }))}
          onChange={(v) => {
            setSituacao(v);
            setPagina(0);
          }}
        />
        {DIMENSOES.map((d) => (
          <FiltroOpcoes
            key={d}
            dimensao={d}
            valor={chaveDoFiltro(filtros, d) ?? ""}
            onChange={(v) => {
              // Trocar o responsável limpa o problema, que pode não ser daquele responsável.
              setFiltros((atuais) => ({
                ...atuais,
                ...(d === "responsavel" ? { cod_solucao: null } : {}),
                ...filtroDaDimensao(d, v),
              }));
              setPagina(0);
            }}
            clientId={clientId}
            nomeLoja={nomeLoja}
            filtro={d === "subproblema" ? { cod_responsavel: filtros.cod_responsavel ?? null } : SEM_FILTRO}
          />
        ))}
        <Button size="small" onClick={limparFiltros} disabled={!temFiltro} sx={{ alignSelf: "center" }}>
          Limpar filtros
        </Button>
      </Stack>

      {erro && <Alert severity="error">{erro}</Alert>}
      {loading && pagina === 0 ? (
        <LinearProgress />
      ) : produtos.length === 0 && !erro ? (
        <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
          Não há produtos para apresentar.
        </Typography>
      ) : (
        <Stack gap={1}>
          {produtos.map((p) => (
            <ProdutoCard key={`${p.cod_empresa}-${p.cod_produto}`} produto={p} nomeLoja={nomeLoja} onVerDetalhe={() => onVerDetalhe(p)} />
          ))}
        </Stack>
      )}
      {temMais && (
        <Box sx={{ textAlign: "center" }}>
          <Button onClick={() => setPagina((p) => p + 1)} disabled={loading}>
            {loading ? "Carregando..." : "Carregar mais"}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
